/**
 * BattleshipAI — AI opponent with three difficulty levels
 *
 * Implements distinct targeting strategies for Easy, Medium, and Hard difficulties:
 * - Easy: Random targeting from unattacked cells
 * - Medium: Hunt/target mode — random in hunt, adjacent targeting on hits
 * - Hard: Probability density mapping with checkerboard bias and strategic placement
 *
 * Requirements: 11.1–11.4, 12.1–12.6, 13.1–13.6
 */

import {
  BattleshipDifficulty,
  Coordinate,
  TrackingGrid,
  TrackingCellState,
  ShipPlacement,
  ShipStatus,
  ShipType,
  Orientation,
  GRID_ROWS,
  GRID_COLS,
  SHIP_SIZES,
  isValidCoordinate,
  getShipCells,
} from './types';
import { BattleshipEngine } from './BattleshipEngine';

// ============================================================================
// All five ship types in standard order (descending size for placement)
// ============================================================================

const ALL_SHIP_TYPES: ShipType[] = [
  'carrier',
  'battleship',
  'cruiser',
  'submarine',
  'destroyer',
];

// ============================================================================
// Internal types for hunt/target state
// ============================================================================

type AIMode = 'hunt' | 'target';

interface HitRecord {
  coordinate: Coordinate;
  shipType?: ShipType; // set when the ship is identified as sunk
  sunk: boolean;
}

// ============================================================================
// BattleshipAI
// ============================================================================

export class BattleshipAI {
  difficulty: BattleshipDifficulty;

  // Internal state for Medium and Hard AI
  private mode: AIMode = 'hunt';
  private hitRecords: HitRecord[] = [];

  constructor(difficulty: BattleshipDifficulty = 'easy') {
    this.difficulty = difficulty;
  }

  // ==========================================================================
  // Difficulty Configuration (Task 6.1)
  // ==========================================================================

  setDifficulty(difficulty: BattleshipDifficulty): void {
    this.difficulty = difficulty;
    this.reset();
  }

  reset(): void {
    this.mode = 'hunt';
    this.hitRecords = [];
  }

  // ==========================================================================
  // Ship Placement Generation (Task 6.1)
  // ==========================================================================

  /**
   * Generates ship placements for the AI player.
   * Easy/Medium: uses engine's autoPlaceShips for random valid positions.
   * Hard: strategic placement — avoid edges, vary orientation, space ships apart.
   */
  generatePlacement(engine: BattleshipEngine): ShipPlacement[] {
    if (this.difficulty === 'hard') {
      return this.generateStrategicPlacement(engine);
    }

    // Easy and Medium: random valid positions via engine
    const result = engine.autoPlaceShips('player2');
    return result.placements || [];
  }

  /**
   * Strategic placement for Hard AI:
   * - Avoid edges (prefer inner cells rows 1-8, cols 1-8)
   * - Vary orientation (alternate between horizontal and vertical)
   * - Space ships apart (prefer cells not adjacent to existing ships)
   */
  private generateStrategicPlacement(engine: BattleshipEngine): ShipPlacement[] {
    // Clear any existing placements
    engine.clearAllShips('player2');

    const orientations: Orientation[] = ['horizontal', 'vertical'];
    let lastOrientation: Orientation = 'horizontal';

    for (const shipType of ALL_SHIP_TYPES) {
      const size = SHIP_SIZES[shipType];
      let placed = false;

      // Alternate orientation for variety
      const preferredOrientation: Orientation =
        lastOrientation === 'horizontal' ? 'vertical' : 'horizontal';

      // Try strategic positions first (inner cells, spaced apart)
      const candidates = this.getStrategicCandidates(
        engine,
        shipType,
        size,
        preferredOrientation
      );

      for (const candidate of candidates) {
        const validation = engine.isPlacementValid('player2', candidate);
        if (validation.valid) {
          engine.placeShip('player2', candidate);
          lastOrientation = candidate.orientation;
          placed = true;
          break;
        }
      }

      // Fallback: try the other orientation with strategic positions
      if (!placed) {
        const fallbackOrientation: Orientation =
          preferredOrientation === 'horizontal' ? 'vertical' : 'horizontal';
        const fallbackCandidates = this.getStrategicCandidates(
          engine,
          shipType,
          size,
          fallbackOrientation
        );

        for (const candidate of fallbackCandidates) {
          const validation = engine.isPlacementValid('player2', candidate);
          if (validation.valid) {
            engine.placeShip('player2', candidate);
            lastOrientation = candidate.orientation;
            placed = true;
            break;
          }
        }
      }

      // Last resort: random placement
      if (!placed) {
        for (let attempt = 0; attempt < 1000; attempt++) {
          const orientation = orientations[Math.floor(Math.random() * 2)];
          const maxRow =
            orientation === 'vertical' ? GRID_ROWS - size : GRID_ROWS - 1;
          const maxCol =
            orientation === 'horizontal' ? GRID_COLS - size : GRID_COLS - 1;

          const row = Math.floor(Math.random() * (maxRow + 1));
          const col = Math.floor(Math.random() * (maxCol + 1));

          const placement: ShipPlacement = {
            shipType,
            startCell: { row, col },
            orientation,
          };

          const validation = engine.isPlacementValid('player2', placement);
          if (validation.valid) {
            engine.placeShip('player2', placement);
            lastOrientation = orientation;
            placed = true;
            break;
          }
        }
      }

      if (!placed) {
        // Should never happen, but fallback to engine auto-place
        engine.clearAllShips('player2');
        const result = engine.autoPlaceShips('player2');
        return result.placements || [];
      }
    }

    return engine.getPlacedShips('player2');
  }

  /**
   * Generate strategic candidate placements for a ship.
   * Prefers inner cells (avoids edges), shuffled for randomness.
   */
  private getStrategicCandidates(
    engine: BattleshipEngine,
    shipType: ShipType,
    size: number,
    orientation: Orientation
  ): ShipPlacement[] {
    const candidates: Array<{ placement: ShipPlacement; score: number }> = [];

    const maxRow = orientation === 'vertical' ? GRID_ROWS - size : GRID_ROWS - 1;
    const maxCol = orientation === 'horizontal' ? GRID_COLS - size : GRID_COLS - 1;

    for (let row = 0; row <= maxRow; row++) {
      for (let col = 0; col <= maxCol; col++) {
        const placement: ShipPlacement = {
          shipType,
          startCell: { row, col },
          orientation,
        };

        const cells = getShipCells(placement);
        let score = 0;

        // Prefer inner cells (avoid row 0, row 9, col 0, col 9)
        for (const cell of cells) {
          if (cell.row > 0 && cell.row < GRID_ROWS - 1) score += 2;
          if (cell.col > 0 && cell.col < GRID_COLS - 1) score += 2;
        }

        // Prefer cells not adjacent to existing ships (spacing)
        const oceanGrid = engine.getOceanGrid('player2');
        let adjacentShipCells = 0;
        for (const cell of cells) {
          const neighbors = [
            { row: cell.row - 1, col: cell.col },
            { row: cell.row + 1, col: cell.col },
            { row: cell.row, col: cell.col - 1 },
            { row: cell.row, col: cell.col + 1 },
          ];
          for (const n of neighbors) {
            if (
              n.row >= 0 &&
              n.row < GRID_ROWS &&
              n.col >= 0 &&
              n.col < GRID_COLS &&
              oceanGrid[n.row][n.col] === 'ship'
            ) {
              adjacentShipCells++;
            }
          }
        }
        // Penalize adjacency to existing ships
        score -= adjacentShipCells * 3;

        candidates.push({ placement, score });
      }
    }

    // Sort by score descending, then shuffle among equal scores
    candidates.sort((a, b) => b.score - a.score);

    // Take top candidates and shuffle them for randomness
    const topScore = candidates.length > 0 ? candidates[0].score : 0;
    const topCandidates = candidates.filter(c => c.score >= topScore - 2);
    this.shuffleArray(topCandidates);

    return topCandidates.map(c => c.placement);
  }

  // ==========================================================================
  // Attack Coordinate Selection (Tasks 6.2, 6.3, 6.4)
  // ==========================================================================

  /**
   * Selects an attack coordinate based on the current difficulty level.
   * Updates internal hunt/target state for Medium and Hard AI.
   */
  selectAttackCoordinate(
    trackingGrid: TrackingGrid,
    remainingShips: ShipStatus[]
  ): Coordinate {
    // First, update internal state based on tracking grid
    this.updateInternalState(trackingGrid, remainingShips);

    switch (this.difficulty) {
      case 'easy':
        return this.selectEasyAttack(trackingGrid);
      case 'medium':
        return this.selectMediumAttack(trackingGrid, remainingShips);
      case 'hard':
        return this.selectHardAttack(trackingGrid, remainingShips);
      default:
        return this.selectEasyAttack(trackingGrid);
    }
  }

  // ==========================================================================
  // Easy AI — Random Targeting (Task 6.2)
  // ==========================================================================

  /**
   * Easy AI: Select a random unattacked cell.
   * Requirements: 11.2, 11.3, 11.4
   */
  private selectEasyAttack(trackingGrid: TrackingGrid): Coordinate {
    const unattacked = this.getUnattackedCells(trackingGrid);

    if (unattacked.length === 0) {
      // Should never happen in a valid game, but return (0,0) as fallback
      return { row: 0, col: 0 };
    }

    const index = Math.floor(Math.random() * unattacked.length);
    return unattacked[index];
  }

  // ==========================================================================
  // Medium AI — Hunt/Target Mode (Task 6.3)
  // ==========================================================================

  /**
   * Medium AI: Hunt/target state machine.
   * Hunt mode: random from unattacked cells.
   * Target mode: attack adjacent cells of unsunk hits, with axis alignment.
   * Requirements: 12.2, 12.3, 12.4, 12.5, 12.6
   */
  private selectMediumAttack(
    trackingGrid: TrackingGrid,
    remainingShips: ShipStatus[]
  ): Coordinate {
    if (this.mode === 'target') {
      const targetCoord = this.selectTargetModeAttack(trackingGrid);
      if (targetCoord) {
        return targetCoord;
      }
      // No valid target found, fall back to hunt mode
      this.mode = 'hunt';
    }

    // Hunt mode: random from unattacked cells
    return this.selectEasyAttack(trackingGrid);
  }

  /**
   * Target mode: find adjacent cells to unsunk hits.
   * If 2+ hits are aligned, continue along the same axis.
   */
  private selectTargetModeAttack(
    trackingGrid: TrackingGrid
  ): Coordinate | null {
    const unsunkHits = this.getUnsunkHitCoordinates(trackingGrid);

    if (unsunkHits.length === 0) {
      return null;
    }

    // If we have 2+ aligned unsunk hits, try to continue along the axis
    if (unsunkHits.length >= 2) {
      const axisTarget = this.getAxisAlignedTarget(trackingGrid, unsunkHits);
      if (axisTarget) {
        return axisTarget;
      }
    }

    // Try adjacent cells of each unsunk hit
    const adjacentTargets = this.getAdjacentTargets(trackingGrid, unsunkHits);
    if (adjacentTargets.length > 0) {
      const index = Math.floor(Math.random() * adjacentTargets.length);
      return adjacentTargets[index];
    }

    return null;
  }

  /**
   * Get coordinates of hits that are not part of a sunk ship.
   */
  private getUnsunkHitCoordinates(trackingGrid: TrackingGrid): Coordinate[] {
    const unsunkHits: Coordinate[] = [];
    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        if (trackingGrid[row][col] === 'hit') {
          unsunkHits.push({ row, col });
        }
      }
    }
    return unsunkHits;
  }

  /**
   * When 2+ hits are aligned (same row or same column), try to extend along that axis.
   */
  private getAxisAlignedTarget(
    trackingGrid: TrackingGrid,
    unsunkHits: Coordinate[]
  ): Coordinate | null {
    // Group hits by row and column to find alignments
    const rowGroups = new Map<number, Coordinate[]>();
    const colGroups = new Map<number, Coordinate[]>();

    for (const hit of unsunkHits) {
      if (!rowGroups.has(hit.row)) rowGroups.set(hit.row, []);
      rowGroups.get(hit.row)!.push(hit);

      if (!colGroups.has(hit.col)) colGroups.set(hit.col, []);
      colGroups.get(hit.col)!.push(hit);
    }

    // Check for horizontal alignment (same row, 2+ hits)
    for (const [row, hits] of Array.from(rowGroups.entries())) {
      if (hits.length >= 2) {
        hits.sort((a: any, b: any) => a.col - b.col);
        // Try extending right (after the rightmost hit)
        const rightCol = hits[hits.length - 1].col + 1;
        if (this.isValidTarget(trackingGrid, { row, col: rightCol })) {
          return { row, col: rightCol };
        }
        // Try extending left (before the leftmost hit)
        const leftCol = hits[0].col - 1;
        if (this.isValidTarget(trackingGrid, { row, col: leftCol })) {
          return { row, col: leftCol };
        }
      }
    }

    // Check for vertical alignment (same column, 2+ hits)
    for (const [col, hits] of Array.from(colGroups.entries())) {
      if (hits.length >= 2) {
        hits.sort((a: any, b: any) => a.row - b.row);
        // Try extending down (after the bottommost hit)
        const downRow = hits[hits.length - 1].row + 1;
        if (this.isValidTarget(trackingGrid, { row: downRow, col })) {
          return { row: downRow, col };
        }
        // Try extending up (before the topmost hit)
        const upRow = hits[0].row - 1;
        if (this.isValidTarget(trackingGrid, { row: upRow, col })) {
          return { row: upRow, col };
        }
      }
    }

    return null;
  }

  /**
   * Get all valid adjacent (up/down/left/right) targets for a set of hit coordinates.
   */
  private getAdjacentTargets(
    trackingGrid: TrackingGrid,
    hits: Coordinate[]
  ): Coordinate[] {
    const targets: Coordinate[] = [];
    const seen = new Set<string>();

    for (const hit of hits) {
      const neighbors: Coordinate[] = [
        { row: hit.row - 1, col: hit.col }, // up
        { row: hit.row + 1, col: hit.col }, // down
        { row: hit.row, col: hit.col - 1 }, // left
        { row: hit.row, col: hit.col + 1 }, // right
      ];

      for (const n of neighbors) {
        const key = `${n.row},${n.col}`;
        if (!seen.has(key) && this.isValidTarget(trackingGrid, n)) {
          targets.push(n);
          seen.add(key);
        }
      }
    }

    return targets;
  }

  // ==========================================================================
  // Hard AI — Probability Density Mapping (Task 6.4)
  // ==========================================================================

  /**
   * Hard AI: Probability density mapping with checkerboard bias.
   * Requirements: 13.2, 13.3, 13.4, 13.5, 13.6
   */
  private selectHardAttack(
    trackingGrid: TrackingGrid,
    remainingShips: ShipStatus[]
  ): Coordinate {
    const densityMap = this.calculateProbabilityDensity(
      trackingGrid,
      remainingShips
    );

    const unsunkHits = this.getUnsunkHitCoordinates(trackingGrid);

    // In target mode: boost scores of cells adjacent to unsunk hits
    if (this.mode === 'target' && unsunkHits.length > 0) {
      for (const hit of unsunkHits) {
        const neighbors: Coordinate[] = [
          { row: hit.row - 1, col: hit.col },
          { row: hit.row + 1, col: hit.col },
          { row: hit.row, col: hit.col - 1 },
          { row: hit.row, col: hit.col + 1 },
        ];
        for (const n of neighbors) {
          if (
            isValidCoordinate(n) &&
            trackingGrid[n.row][n.col] === 'unknown'
          ) {
            densityMap[n.row][n.col] *= 3; // Significant boost for adjacent cells
          }
        }
      }
    }

    // In hunt mode: apply checkerboard bias
    if (this.mode === 'hunt') {
      for (let row = 0; row < GRID_ROWS; row++) {
        for (let col = 0; col < GRID_COLS; col++) {
          if ((row + col) % 2 === 0) {
            densityMap[row][col] *= 1.5; // Checkerboard bias
          }
        }
      }
    }

    // Find the cell with the highest score
    let bestScore = -1;
    let bestCells: Coordinate[] = [];

    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        if (trackingGrid[row][col] !== 'unknown') continue;

        const score = densityMap[row][col];
        if (score > bestScore) {
          bestScore = score;
          bestCells = [{ row, col }];
        } else if (score === bestScore) {
          bestCells.push({ row, col });
        }
      }
    }

    if (bestCells.length === 0) {
      // Fallback: random unattacked cell
      return this.selectEasyAttack(trackingGrid);
    }

    // Pick randomly among tied best cells
    const index = Math.floor(Math.random() * bestCells.length);
    return bestCells[index];
  }

  /**
   * Calculate probability density map.
   * For each unattacked cell, count the number of ways each remaining (unsunk) ship
   * could overlap that cell given known hits and misses.
   */
  calculateProbabilityDensity(
    trackingGrid: TrackingGrid,
    remainingShips: ShipStatus[]
  ): number[][] {
    const density: number[][] = Array.from({ length: GRID_ROWS }, () =>
      Array.from({ length: GRID_COLS }, () => 0)
    );

    // Get remaining (unsunk) ship sizes
    const unsunkShips = remainingShips.filter(s => !s.isSunk);
    const shipSizes = unsunkShips.map(s => s.size);

    for (const size of shipSizes) {
      // Try every possible horizontal placement
      for (let row = 0; row < GRID_ROWS; row++) {
        for (let col = 0; col <= GRID_COLS - size; col++) {
          if (this.canPlaceShipForDensity(trackingGrid, row, col, size, 'horizontal')) {
            for (let i = 0; i < size; i++) {
              density[row][col + i]++;
            }
          }
        }
      }

      // Try every possible vertical placement
      for (let row = 0; row <= GRID_ROWS - size; row++) {
        for (let col = 0; col < GRID_COLS; col++) {
          if (this.canPlaceShipForDensity(trackingGrid, row, col, size, 'vertical')) {
            for (let i = 0; i < size; i++) {
              density[row + i][col]++;
            }
          }
        }
      }
    }

    return density;
  }

  /**
   * Check if a ship of given size could be placed at (row, col) with given orientation,
   * considering known hits and misses on the tracking grid.
   * A ship can overlap 'unknown' cells and 'hit' cells (unsunk hits), but not 'miss' or 'sunk' cells.
   */
  private canPlaceShipForDensity(
    trackingGrid: TrackingGrid,
    startRow: number,
    startCol: number,
    size: number,
    orientation: 'horizontal' | 'vertical'
  ): boolean {
    for (let i = 0; i < size; i++) {
      const row = orientation === 'vertical' ? startRow + i : startRow;
      const col = orientation === 'horizontal' ? startCol + i : startCol;

      if (row >= GRID_ROWS || col >= GRID_COLS) return false;

      const cell = trackingGrid[row][col];
      if (cell === 'miss' || cell === 'sunk') return false;
    }
    return true;
  }

  // ==========================================================================
  // Internal State Management
  // ==========================================================================

  /**
   * Update internal hunt/target state based on the current tracking grid.
   * Transitions: Hunt → Target on unsunk hit, Target → Hunt when no unsunk hits.
   */
  private updateInternalState(
    trackingGrid: TrackingGrid,
    remainingShips: ShipStatus[]
  ): void {
    const unsunkHits = this.getUnsunkHitCoordinates(trackingGrid);

    if (unsunkHits.length > 0) {
      this.mode = 'target';
    } else {
      this.mode = 'hunt';
    }
  }

  // ==========================================================================
  // Utility Methods
  // ==========================================================================

  /**
   * Get all unattacked cells on the tracking grid.
   */
  private getUnattackedCells(trackingGrid: TrackingGrid): Coordinate[] {
    const cells: Coordinate[] = [];
    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        if (trackingGrid[row][col] === 'unknown') {
          cells.push({ row, col });
        }
      }
    }
    return cells;
  }

  /**
   * Check if a coordinate is a valid target (within bounds and not yet attacked).
   */
  private isValidTarget(
    trackingGrid: TrackingGrid,
    coord: Coordinate
  ): boolean {
    return (
      isValidCoordinate(coord) &&
      trackingGrid[coord.row][coord.col] === 'unknown'
    );
  }

  /**
   * Fisher-Yates shuffle for an array (in-place).
   */
  private shuffleArray<T>(array: T[]): void {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }

  /**
   * Get the current AI mode (for testing purposes).
   */
  getMode(): AIMode {
    return this.mode;
  }
}
